import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import ListaPrecioSearch from '../../../components/ListaPrecioSearch';
import SucursalVentaSearch from '../../../components/SucursalVentaSearch';
import TipoMonedaSearch from '../../../components/TipoMonedaSearch';
import { useSucursalesUsuarioContext } from '../../../contexts/SucursalesUsuarioContext';
import { ROUTES } from '../../../config/routes';
import companiaService from '../../../services/companiaService';
import ubicacionService, { etiquetaUbicacionSeleccion } from '../../../services/ubicacionService';
import { persistGlobalIdCompania } from '../../../utils/companiaPreferida';
import {
  persistCompaniaYSucursalInicial,
  resolveCompaniaYSucursalInicial,
  resolveNroSucParaCompania,
} from '../../../utils/sucursalPreferida';
import contratoVentaService, {
  ContratoCertificacion,
  ContratoCreateUpdate,
  ContratoDetalle,
  ContratoItem,
  ContratoPeriodo,
  ContratoTipo,
} from '../../../services/contratoVentaService';
import FacturaPOSClientePanel from '../../facturaventa/pos/FacturaPOSClientePanel';
import { POSCliente } from '../../facturaventa/pos/types';
import ContratoTiposDialog from './ContratoTiposDialog';
import ContratoCertificacionesTab from './ContratoCertificacionesTab';
import ContratoItemsSection from './ContratoItemsSection';
import ContratoPeriodosTab from './ContratoPeriodosTab';
import ContratoPOSPanel, { ContratoPOSPanelForm } from './ContratoPOSPanel';
import ContratoVariablesChips from './ContratoVariablesChips';
import {
  CONTRATO_FIELD_DESC,
  CONTRATO_FIELD_OBS,
  captureContratoPlantillaCaret,
  insertAtCaret,
  restoreContratoPlantillaCaret,
  type ContratoPlantillaCaret,
} from './contratoPlantillaVariables';

interface FormState extends ContratoPOSPanelForm {
  nroCliente: number;
  nombreCliente: string;
  items: ContratoItem[];
}

const today = () => new Date().toISOString().slice(0, 10);
const yearEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), 11, 31).toISOString().slice(0, 10);
};

const defaultForm = (): FormState => ({
  nroCliente: 0,
  nombreCliente: '',
  codigoMoneda: 0,
  monedaDescripcion: 'Pesos',
  codigoLista: '',
  idCompania: '',
  nroSucursalVenta: 0,
  sucursalNombre: '',
  nroUbicacionOrigen: 0,
  ubicacionLabel: '',
  fechaInicio: today(),
  fechaFin: yearEnd(),
  periodicidad: 'Mensual',
  modoFacturacion: 'FinPeriodo',
  diaFacturacion: '',
  nroOrdenCompraCliente: '',
  tipoContratoId: '',
  codigoContrato: '',
  observaciones: '',
  estado: 'Borrador',
  items: [],
});

const estimadoItems = (items: ContratoItem[]): number =>
  items.reduce((acc, it) => {
    const base = Number(it.cantidad || 0) * Number(it.precioUnitario || 0);
    const bonif = Math.min(100, Math.max(0, Number(it.bonificacion || 0)));
    return acc + base * (1 - bonif / 100);
  }, 0);

const ContratoForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id || id === 'nuevo';
  const contratoId = isNew ? 0 : Number(id);
  const { enqueueSnackbar } = useSnackbar();

  const [form, setForm] = useState<FormState>(defaultForm());
  const [periodos, setPeriodos] = useState<ContratoPeriodo[]>([]);
  const [certificaciones, setCertificaciones] = useState<ContratoCertificacion[]>([]);
  const [numero, setNumero] = useState<number | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [monedaOpen, setMonedaOpen] = useState(false);
  const [sucursalOpen, setSucursalOpen] = useState(false);
  const [listaOpen, setListaOpen] = useState(false);
  const [companias, setCompanias] = useState<Array<{ idCompania: string; nombre: string }>>([]);
  const [tiposContrato, setTiposContrato] = useState<ContratoTipo[]>([]);
  const [tiposOpen, setTiposOpen] = useState(false);
  const lastPlantillaCaretRef = useRef<ContratoPlantillaCaret | null>(null);
  const initCompaniaSucursalRef = useRef(false);
  const {
    sucursales,
    preferidaNro,
    preferidaSucursal,
    loading: loadingSucursalesUsuario,
  } = useSucursalesUsuarioContext();

  const patchForm = useCallback((patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  const trackPlantillaCaret = useCallback((caret: ContratoPlantillaCaret) => {
    lastPlantillaCaretRef.current = caret;
  }, []);

  const handleInsertVariable = useCallback(
    (variable: string) => {
      const caret = captureContratoPlantillaCaret(lastPlantillaCaretRef.current);
      if (!caret) {
        enqueueSnackbar(
          'Posicioná el cursor en Observaciones o en la descripción de un producto',
          { variant: 'info' },
        );
        return;
      }

      if (caret.field === CONTRATO_FIELD_OBS) {
        const next = insertAtCaret(form.observaciones, caret.start, caret.end, variable);
        setForm((prev) => ({ ...prev, observaciones: next.value }));
        lastPlantillaCaretRef.current = {
          field: CONTRATO_FIELD_OBS,
          start: next.caret,
          end: next.caret,
        };
        restoreContratoPlantillaCaret(caret, next.caret);
        return;
      }

      if (caret.itemIndex == null) return;
      const item = form.items[caret.itemIndex];
      if (!item) return;
      const next = insertAtCaret(item.descripcionPlantilla, caret.start, caret.end, variable);
      setForm((prev) => ({
        ...prev,
        items: prev.items.map((it, i) =>
          i === caret.itemIndex ? { ...it, descripcionPlantilla: next.value } : it,
        ),
      }));
      lastPlantillaCaretRef.current = {
        field: CONTRATO_FIELD_DESC,
        itemIndex: caret.itemIndex,
        start: next.caret,
        end: next.caret,
      };
      restoreContratoPlantillaCaret(caret, next.caret);
    },
    [enqueueSnackbar, form.items, form.observaciones],
  );

  const load = useCallback(async () => {
    if (!contratoId) return;
    try {
      setLoading(true);
      const d: ContratoDetalle = await contratoVentaService.getById(contratoId);
      setNumero(d.numero);

      let ubicacionLabel = '';
      const nroUb = Number(d.nroUbicacionOrigen || 0);
      if (nroUb > 0) {
        try {
          const u = await ubicacionService.getUbicacion(nroUb);
          ubicacionLabel = etiquetaUbicacionSeleccion(u);
        } catch {
          ubicacionLabel = `Ubicación (${nroUb})`;
        }
      }

      const sucNombre =
        sucursales.find((s) => Number(s.nroSuc) === d.nroSucursalVenta)?.nombreSucursal?.trim() ||
        String(d.nroSucursalVenta);

      setForm({
        nroCliente: d.nroCliente,
        nombreCliente: d.nombreCliente || '',
        codigoMoneda: d.codigoMoneda,
        monedaDescripcion: d.monedaDescripcion || '',
        codigoLista: d.codigoLista || '',
        idCompania: String(d.idCompania ?? '').trim(),
        nroSucursalVenta: d.nroSucursalVenta,
        sucursalNombre: sucNombre,
        nroUbicacionOrigen: nroUb,
        ubicacionLabel,
        fechaInicio: String(d.fechaInicio).slice(0, 10),
        fechaFin: String(d.fechaFin).slice(0, 10),
        periodicidad: d.periodicidad,
        modoFacturacion: d.modoFacturacion,
        diaFacturacion: d.diaFacturacion != null ? String(d.diaFacturacion) : '',
        nroOrdenCompraCliente: d.nroOrdenCompraCliente || '',
        tipoContratoId: d.tipoContratoId || '',
        codigoContrato: d.codigoContrato || '',
        observaciones: d.observaciones || '',
        estado: d.estado,
        items: d.items,
      });
      setPeriodos(d.periodos || []);
      setCertificaciones(d.certificaciones || []);
    } catch {
      enqueueSnackbar('Error al cargar el contrato', { variant: 'error' });
      navigate(ROUTES.ventas.contratos.list);
    } finally {
      setLoading(false);
    }
  }, [contratoId, enqueueSnackbar, navigate, sucursales]);

  useEffect(() => {
    if (!isNew) void load();
  }, [isNew, load]);

  useEffect(() => {
    const cargarCompanias = async () => {
      try {
        const lista = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
        setCompanias(
          lista
            .map((c) => ({
              idCompania: String(c.idCompania ?? '').trim(),
              nombre: String(c.nombre ?? '').trim(),
            }))
            .filter((c) => Boolean(c.idCompania)),
        );
      } catch {
        setCompanias([]);
      }
    };
    void cargarCompanias();
  }, []);

  const cargarTipos = useCallback(async () => {
    try {
      setTiposContrato(await contratoVentaService.getTipos());
    } catch {
      setTiposContrato([]);
    }
  }, []);

  useEffect(() => {
    void cargarTipos();
  }, [cargarTipos]);

  useEffect(() => {
    if (!isNew) return;
    if (loadingSucursalesUsuario) return;
    if (sucursales.length === 0 || companias.length === 0) return;
    if (initCompaniaSucursalRef.current) return;
    const inicial = resolveCompaniaYSucursalInicial({
      sucursales,
      preferidaNro,
      preferidaSucursal,
      companias,
    });
    if (!inicial) return;
    initCompaniaSucursalRef.current = true;
    const suc = sucursales.find((s) => Number(s.nroSuc) === inicial.nroSuc);
    setForm((prev) => ({
      ...prev,
      idCompania: inicial.idCompania,
      nroSucursalVenta: inicial.nroSuc,
      sucursalNombre: suc?.nombreSucursal?.trim() || String(inicial.nroSuc),
    }));
    persistCompaniaYSucursalInicial(inicial);
  }, [isNew, loadingSucursalesUsuario, sucursales, companias, preferidaNro, preferidaSucursal]);

  useEffect(() => {
    if (isNew || loading) return;
    if (form.idCompania.trim() || !form.nroSucursalVenta) return;
    const suc = sucursales.find((s) => Number(s.nroSuc) === form.nroSucursalVenta);
    const idComp = String(suc?.idCompania ?? '').trim();
    if (!idComp) return;
    setForm((prev) => (prev.idCompania.trim() ? prev : { ...prev, idCompania: idComp }));
  }, [isNew, loading, form.idCompania, form.nroSucursalVenta, sucursales]);

  const handleCompaniaChange = (value: string) => {
    const idComp = String(value ?? '').trim();
    if (idComp) persistGlobalIdCompania(idComp);
    const nro = resolveNroSucParaCompania({
      idCompania: idComp,
      sucursales,
      preferidaNro,
      storedNroSuc: Number(localStorage.getItem('nroSuc') || '0') || null,
      currentNroSuc: form.nroSucursalVenta,
      keepCurrentIfValid: false,
    });
    const suc = nro ? sucursales.find((s) => Number(s.nroSuc) === nro) : undefined;
    patchForm({
      idCompania: idComp,
      nroSucursalVenta: nro && nro > 0 ? nro : 0,
      sucursalNombre: suc?.nombreSucursal?.trim() || (nro && nro > 0 ? String(nro) : ''),
    });
  };

  const handlePanelChange = (patch: Partial<ContratoPOSPanelForm>) => {
    if (Object.prototype.hasOwnProperty.call(patch, 'idCompania') && patch.idCompania !== undefined) {
      handleCompaniaChange(patch.idCompania);
      const rest = { ...patch };
      delete rest.idCompania;
      delete rest.nroSucursalVenta;
      delete rest.sucursalNombre;
      if (Object.keys(rest).length > 0) patchForm(rest);
      return;
    }
    patchForm(patch);
  };

  const buildDto = (activar?: boolean): ContratoCreateUpdate => ({
    nroCliente: form.nroCliente,
    codigoMoneda: form.codigoMoneda,
    codigoLista: form.codigoLista || null,
    idCompania: form.idCompania.trim(),
    nroSucursalVenta: form.nroSucursalVenta,
    nroUbicacionOrigen: form.nroUbicacionOrigen,
    fechaInicio: form.fechaInicio,
    fechaFin: form.fechaFin,
    periodicidad: form.periodicidad,
    modoFacturacion: form.modoFacturacion,
    diaFacturacion: form.diaFacturacion ? Number(form.diaFacturacion) : null,
    nroOrdenCompraCliente: form.nroOrdenCompraCliente || null,
    tipoContratoId: form.tipoContratoId ? Number(form.tipoContratoId) : null,
    codigoContrato: form.codigoContrato.trim() || null,
    observaciones: form.observaciones || null,
    activar: activar === true,
    items: form.items.map((it, idx) => ({ ...it, orden: idx + 1 })),
  });

  const handleSave = async (activar?: boolean) => {
    if (!form.nroCliente) {
      enqueueSnackbar('Seleccioná un cliente', { variant: 'warning' });
      return;
    }
    if (!form.idCompania.trim()) {
      enqueueSnackbar('Seleccioná una compañía', { variant: 'warning' });
      return;
    }
    if (!form.nroSucursalVenta) {
      enqueueSnackbar('Seleccioná una sucursal de venta', { variant: 'warning' });
      return;
    }
    if (!form.nroUbicacionOrigen) {
      enqueueSnackbar('Seleccioná una ubicación de origen', { variant: 'warning' });
      return;
    }
    if (form.items.length === 0) {
      enqueueSnackbar('Agregá al menos un producto', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      const dto = buildDto(activar);
      if (isNew) {
        await contratoVentaService.create(dto);
        enqueueSnackbar('Contrato creado', { variant: 'success' });
      } else {
        await contratoVentaService.update(contratoId, dto);
        enqueueSnackbar('Contrato actualizado', { variant: 'success' });
      }
      navigate(ROUTES.ventas.contratos.list);
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEstado = async (estado: string) => {
    try {
      await contratoVentaService.cambiarEstado(contratoId, estado);
      enqueueSnackbar(`Estado: ${estado}`, { variant: 'success' });
      await load();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al cambiar estado', {
        variant: 'error',
      });
    }
  };

  const posCliente: POSCliente | null = useMemo(() => {
    if (!form.nroCliente) return null;
    return {
      nroCliente: form.nroCliente,
      nombre: form.nombreCliente || '',
      nroLista: form.codigoLista || undefined,
    };
  }, [form.nroCliente, form.nombreCliente, form.codigoLista]);

  const handleClienteSelect = (cli: POSCliente) => {
    patchForm({
      nroCliente: cli.nroCliente,
      nombreCliente: cli.nombre || '',
      codigoLista: form.codigoLista || cli.nroLista || cli.listaPrecio?.codigoLista || '',
    });
  };

  const readOnly = form.estado === 'Anulado';
  const totalEstimado = useMemo(() => estimadoItems(form.items), [form.items]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mb: 2 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(ROUTES.ventas.contratos.list)}
              sx={{ textTransform: 'none' }}
            >
              Contratos
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              {isNew ? 'Nuevo contrato' : `Contrato Nº ${numero}`}
            </Typography>
            {!isNew && (
              <Chip
                size="small"
                label={form.estado}
                color={form.estado === 'Vigente' ? 'success' : 'default'}
              />
            )}
          </Stack>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="overline" color="text.secondary" display="block" mb={0.75}>
                    Cliente
                  </Typography>
                  <FacturaPOSClientePanel
                    cliente={posCliente}
                    onClienteSelect={handleClienteSelect}
                    onClienteClear={
                      readOnly
                        ? undefined
                        : () => patchForm({ nroCliente: 0, nombreCliente: '' })
                    }
                    readOnly={readOnly}
                    ordenCompra={form.nroOrdenCompraCliente}
                  />
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                    <Typography variant="overline" color="text.secondary">
                      Productos
                    </Typography>
                    {form.items.length > 0 && (
                      <Chip
                        label={`${form.items.length} renglón${form.items.length > 1 ? 'es' : ''}`}
                        size="small"
                      />
                    )}
                  </Stack>
                  {!readOnly && (
                    <Box sx={{ mb: 1 }}>
                      <ContratoVariablesChips onInsert={handleInsertVariable} />
                    </Box>
                  )}
                  <ContratoItemsSection
                    items={form.items}
                    onChange={(items) => patchForm({ items })}
                    onTrackPlantillaCaret={trackPlantillaCaret}
                    readOnly={readOnly}
                  />
                </Box>

                {!isNew && (
                  <Box>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
                      <Tab label="Períodos" />
                      <Tab label="Certificaciones" />
                    </Tabs>
                    {tab === 0 && (
                      <ContratoPeriodosTab
                        contratoId={contratoId}
                        periodos={periodos}
                        onUpdated={setPeriodos}
                        readOnly={readOnly}
                      />
                    )}
                    {tab === 1 && (
                      <ContratoCertificacionesTab
                        contratoId={contratoId}
                        items={form.items}
                        certificaciones={certificaciones}
                        onChanged={load}
                        readOnly={readOnly}
                      />
                    )}
                  </Box>
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <ContratoPOSPanel
                  form={form}
                  onChange={handlePanelChange}
                  companias={companias}
                  tiposContrato={tiposContrato}
                  readOnly={readOnly}
                  isNew={isNew}
                  saving={saving}
                  cantItems={form.items.length}
                  totalEstimado={totalEstimado}
                  onOpenMoneda={() => setMonedaOpen(true)}
                  onOpenSucursal={() => setSucursalOpen(true)}
                  onOpenLista={() => setListaOpen(true)}
                  onOpenTipos={() => setTiposOpen(true)}
                  onTrackObsCaret={(start, end) =>
                    trackPlantillaCaret({ field: CONTRATO_FIELD_OBS, start, end })
                  }
                  onGuardar={(activar) => void handleSave(activar)}
                  onActivar={() => void handleEstado('Vigente')}
                  onSuspender={() => void handleEstado('Suspendido')}
                  onReanudar={() => void handleEstado('Vigente')}
                  onAnular={() => void handleEstado('Anulado')}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TipoMonedaSearch
        open={monedaOpen}
        onClose={() => setMonedaOpen(false)}
        onSelect={(m: any) => {
          patchForm({
            codigoMoneda: Number(m.codigoMoneda),
            monedaDescripcion: m.descripcion || String(m.codigoMoneda),
          });
          setMonedaOpen(false);
        }}
      />
      <SucursalVentaSearch
        open={sucursalOpen}
        onClose={() => setSucursalOpen(false)}
        idCompania={form.idCompania}
        onSelect={(s) => {
          const idFromSuc = String(s.idCompania ?? '').trim();
          if (idFromSuc) persistGlobalIdCompania(idFromSuc);
          patchForm({
            nroSucursalVenta: s.nroSuc,
            sucursalNombre: s.nombreSucursal || String(s.nroSuc),
            ...(idFromSuc ? { idCompania: idFromSuc } : {}),
          });
          setSucursalOpen(false);
        }}
      />
      <ContratoTiposDialog
        open={tiposOpen}
        onClose={() => setTiposOpen(false)}
        onChanged={() => {
          void cargarTipos();
        }}
      />
      <ListaPrecioSearch
        open={listaOpen}
        onClose={() => setListaOpen(false)}
        onSelect={(l) => {
          patchForm({ codigoLista: l.codigoLista });
          setListaOpen(false);
        }}
      />
    </Box>
  );
};

export default ContratoForm;
